import { esquemaProceso, esquemaPuesto, esquemaTipoDocumento } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { notFound, useRouteContext } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Modal } from "../componentes/ModalFirma.tsx";
import {
  Aviso,
  Boton,
  CABECERA,
  Campo,
  CLASE_ENTRADA,
  EstadoVacio,
  Pildora,
  Tabla,
  TD,
  TH,
} from "../componentes/ui.tsx";
import type {
  CatalogosAdmin,
  ProcesoAdministrado,
  PuestoAdministrado,
  TipoAdministrado,
} from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

/**
 * "Procesos, tipos y puestos": los dos catálogos que forman el código de cada documento
 * (`PROCESO-TIPO-NNN`) y el listado de puestos aprobados (DIR-ESP-004), del que se elige el
 * puesto de cada persona. Solo un administrador. Lo que ya está en un código no cambia; nada se
 * borra, se desactiva. Las personas se asignan a cada proceso y a su puesto desde "Usuarios".
 */

type FormularioProceso = { modo: "crear" } | { modo: "editar"; proceso: ProcesoAdministrado };
type FormularioTipo = { modo: "crear" } | { modo: "editar"; tipo: TipoAdministrado };
type FormularioPuesto = { modo: "crear" } | { modo: "editar"; puesto: PuestoAdministrado };

export function PaginaCatalogos() {
  const { perfil } = useRouteContext({ from: "/app" });
  if (perfil.usuario.rolGlobal !== "administrador") throw notFound();

  const trpc = useTRPC();
  const lista = useQuery(trpc.catalogos.listar.queryOptions());
  const [formProceso, setFormProceso] = useState<FormularioProceso | null>(null);
  const [formTipo, setFormTipo] = useState<FormularioTipo | null>(null);
  const [formPuesto, setFormPuesto] = useState<FormularioPuesto | null>(null);
  const [conInactivos, setConInactivos] = useState(false);

  const cerrarYRecargar = () => {
    setFormProceso(null);
    setFormTipo(null);
    setFormPuesto(null);
    queryClient.invalidateQueries();
  };

  return (
    <div className="max-w-[1100px]">
      <Encabezado overline="Administración" titulo="Procesos, tipos y puestos" />
      <p className="mb-4 text-[12.5px] text-texto-3">
        El código de cada documento se arma con el proceso y el tipo (<code>GES-PRO-001</code>). Por
        eso, el código de un proceso o de un tipo que ya tiene documentos no cambia, y nada se
        borra: se desactiva y deja de ofrecerse al crear documentos. Los puestos son los del listado
        aprobado (DIR-ESP-004): el de cada persona se elige de aquí y es lo que sale en sus firmas.
        Quién trabaja en cada proceso, y con qué puesto, se decide en{" "}
        <span className="font-semibold">Usuarios</span>.
      </p>
      <label className="mb-4 flex items-center gap-2 text-[12.5px] text-texto-2">
        <input
          type="checkbox"
          checked={conInactivos}
          onChange={(e) => setConInactivos(e.target.checked)}
        />
        Mostrar desactivados
      </label>

      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.data && (
        <div className="space-y-8">
          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-[16px] font-bold text-marino">Procesos</h2>
              <Boton onClick={() => setFormProceso({ modo: "crear" })}>Nuevo proceso</Boton>
            </div>
            <TablaProcesos
              procesos={lista.data.procesos.filter((p) => conInactivos || p.activo)}
              onEditar={(proceso) => setFormProceso({ modo: "editar", proceso })}
            />
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-[16px] font-bold text-marino">Tipos de documento</h2>
              <Boton onClick={() => setFormTipo({ modo: "crear" })}>Nuevo tipo</Boton>
            </div>
            <TablaTipos
              tipos={lista.data.tipos.filter((t) => conInactivos || t.activo)}
              onEditar={(tipo) => setFormTipo({ modo: "editar", tipo })}
            />
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-[16px] font-bold text-marino">Puestos aprobados</h2>
              <Boton onClick={() => setFormPuesto({ modo: "crear" })}>Nuevo puesto</Boton>
            </div>
            <TablaPuestos
              puestos={lista.data.puestos.filter((p) => conInactivos || p.activo)}
              onEditar={(puesto) => setFormPuesto({ modo: "editar", puesto })}
            />
          </section>
        </div>
      )}

      {formProceso && lista.data && (
        <FormularioDeProceso
          formulario={formProceso}
          personas={lista.data.personas}
          onCerrar={() => setFormProceso(null)}
          onGuardado={cerrarYRecargar}
        />
      )}
      {formTipo && (
        <FormularioDeTipo
          formulario={formTipo}
          onCerrar={() => setFormTipo(null)}
          onGuardado={cerrarYRecargar}
        />
      )}
      {formPuesto && (
        <FormularioDePuesto
          formulario={formPuesto}
          onCerrar={() => setFormPuesto(null)}
          onGuardado={cerrarYRecargar}
        />
      )}
    </div>
  );
}

function TablaProcesos({
  procesos,
  onEditar,
}: {
  procesos: ProcesoAdministrado[];
  onEditar: (p: ProcesoAdministrado) => void;
}) {
  if (procesos.length === 0) return <EstadoVacio>No hay procesos.</EstadoVacio>;
  return (
    <Tabla minimo={720}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Proceso</th>
          <th className={TH}>Dueño</th>
          <th className={TH}>Personas</th>
          <th className={TH}>Documentos</th>
          <th className={TH} />
        </tr>
      </thead>
      <tbody>
        {procesos.map((p) => (
          <tr
            key={p.id}
            className={`border-b border-borde/60 last:border-0 ${p.activo ? "" : "opacity-60"}`}
          >
            <td className={`${TD} font-display font-semibold tracking-wide`}>{p.codigo}</td>
            <td className={TD}>
              <span className="font-semibold">{p.nombre}</span>
              {!p.activo && (
                <Pildora tono="bg-gris-fondo text-gris" className="ml-2">
                  Desactivado
                </Pildora>
              )}
            </td>
            <td className={`${TD} text-texto-2`}>{p.duenoNombre ?? "—"}</td>
            <td className={`${TD} text-texto-2`}>{p.personas}</td>
            <td className={`${TD} text-texto-2`}>{p.documentos}</td>
            <td className={`${TD} text-right`}>
              <Boton variante="secundario" onClick={() => onEditar(p)}>
                Editar
              </Boton>
            </td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function TablaTipos({
  tipos,
  onEditar,
}: {
  tipos: TipoAdministrado[];
  onEditar: (t: TipoAdministrado) => void;
}) {
  if (tipos.length === 0) return <EstadoVacio>No hay tipos.</EstadoVacio>;
  return (
    <Tabla minimo={760}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Tipo</th>
          <th className={TH}>Cómo se maneja</th>
          <th className={TH}>Documentos</th>
          <th className={TH} />
        </tr>
      </thead>
      <tbody>
        {tipos.map((t) => (
          <tr
            key={t.id}
            className={`border-b border-borde/60 last:border-0 ${t.activo ? "" : "opacity-60"}`}
          >
            <td className={`${TD} font-display font-semibold tracking-wide`}>{t.codigo}</td>
            <td className={TD}>
              <span className="font-semibold">{t.nombre}</span>
              {!t.activo && (
                <Pildora tono="bg-gris-fondo text-gris" className="ml-2">
                  Desactivado
                </Pildora>
              )}
            </td>
            <td className={`${TD} text-[12px] text-texto-2`}>
              {t.esRegistro
                ? "Registro: control de registros, sin revisión anual ni acuses"
                : t.requiereAprobacion
                  ? `Documento con ${t.conRevisor ? "tres firmas" : "dos firmas, sin revisor"}${t.tieneRevisionAnual ? " y revisión anual" : ""}`
                  : "Informativo: se publica sin firmas"}
              {t.paraTodaLaEmpresa && " · lo ve y lo recibe toda la empresa"}
            </td>
            <td className={`${TD} text-texto-2`}>{t.documentos}</td>
            <td className={`${TD} text-right`}>
              <Boton variante="secundario" onClick={() => onEditar(t)}>
                Editar
              </Boton>
            </td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function FormularioDeProceso({
  formulario,
  personas,
  onCerrar,
  onGuardado,
}: {
  formulario: FormularioProceso;
  personas: CatalogosAdmin["personas"];
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const trpc = useTRPC();
  const editando = formulario.modo === "editar" ? formulario.proceso : null;
  const codigoFijo = !!editando && editando.documentos > 0;
  const [codigo, setCodigo] = useState(editando?.codigo ?? "");
  const [nombre, setNombre] = useState(editando?.nombre ?? "");
  const [duenoId, setDuenoId] = useState(editando?.duenoId ?? "");
  const [activo, setActivo] = useState(editando?.activo ?? true);
  const [error, setError] = useState<string | null>(null);
  const opciones = { onSuccess: onGuardado, onError: (e: unknown) => setError(mensajeDe(e)) };
  const crear = useMutation(trpc.catalogos.crearProceso.mutationOptions(opciones));
  const actualizar = useMutation(trpc.catalogos.actualizarProceso.mutationOptions(opciones));
  const enviando = crear.isPending || actualizar.isPending;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const datos = esquemaProceso.safeParse({ codigo, nombre, duenoId: duenoId || null, activo });
    if (!datos.success) {
      setError(datos.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    if (editando) actualizar.mutate({ id: editando.id, ...datos.data });
    else crear.mutate(datos.data);
  }

  return (
    <Modal
      titulo={editando ? `Editar el proceso ${editando.codigo}` : "Nuevo proceso"}
      onCerrar={onCerrar}
      bloqueado={enviando}
    >
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <Campo
            etiqueta="Código"
            ayuda={
              codigoFijo
                ? `Ya tiene ${editando?.documentos} documentos: no cambia.`
                : "De dos a cuatro letras."
            }
          >
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className={`${CLASE_ENTRADA} font-display tracking-wide uppercase`}
              maxLength={4}
              disabled={codigoFijo}
              autoComplete="off"
              // biome-ignore lint/a11y/noAutofocus: el formulario se abre para escribir aquí
              autoFocus={!codigoFijo}
            />
          </Campo>
          <Campo etiqueta="Nombre">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={CLASE_ENTRADA}
              autoComplete="off"
            />
          </Campo>
        </div>
        <Campo
          etiqueta="Dueño del proceso"
          ayuda='Le aparece como "Mi proceso" en su menú. Opcional.'
        >
          <select
            value={duenoId}
            onChange={(e) => setDuenoId(e.target.value)}
            className={CLASE_ENTRADA}
          >
            <option value="">Sin dueño</option>
            {personas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre} · {p.puesto}
              </option>
            ))}
          </select>
        </Campo>
        <label className="flex items-center gap-2 text-[12.5px] text-texto-2">
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          Activo (se ofrece al crear documentos y aparece en los menús)
        </label>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={enviando}>
            {enviando ? "Guardando…" : editando ? "Guardar cambios" : "Crear proceso"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

function FormularioDeTipo({
  formulario,
  onCerrar,
  onGuardado,
}: {
  formulario: FormularioTipo;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const trpc = useTRPC();
  const editando = formulario.modo === "editar" ? formulario.tipo : null;
  const naturalezaFija = !!editando && editando.documentos > 0;
  const [codigo, setCodigo] = useState(editando?.codigo ?? "");
  const [nombre, setNombre] = useState(editando?.nombre ?? "");
  const [esRegistro, setEsRegistro] = useState(editando?.esRegistro ?? false);
  const [tieneRevisionAnual, setTieneRevisionAnual] = useState(
    editando?.tieneRevisionAnual ?? true,
  );
  const [requiereAprobacion, setRequiereAprobacion] = useState(
    editando?.requiereAprobacion ?? true,
  );
  /** Ruta corta (2026-10-29): sin revisor, sus documentos llevan solo elaboró y aprobó. */
  const [conRevisor, setConRevisor] = useState(editando?.conRevisor ?? true);
  const [paraTodaLaEmpresa, setParaTodaLaEmpresa] = useState(editando?.paraTodaLaEmpresa ?? false);
  const [activo, setActivo] = useState(editando?.activo ?? true);
  const [error, setError] = useState<string | null>(null);
  const opciones = { onSuccess: onGuardado, onError: (e: unknown) => setError(mensajeDe(e)) };
  const crear = useMutation(trpc.catalogos.crearTipo.mutationOptions(opciones));
  const actualizar = useMutation(trpc.catalogos.actualizarTipo.mutationOptions(opciones));
  const enviando = crear.isPending || actualizar.isPending;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const datos = esquemaTipoDocumento.safeParse({
      codigo,
      nombre,
      esRegistro,
      // Un registro no tiene revisión anual ni acuses: lo decide el tipo, no quien lo crea.
      tieneRevisionAnual: esRegistro ? false : tieneRevisionAnual,
      requiereAprobacion,
      // Sin ruta de firmas no hay revisor que quitar.
      conRevisor: requiereAprobacion ? conRevisor : true,
      // Un registro se llena en hojas por el personal del proceso: no es un comunicado.
      paraTodaLaEmpresa: esRegistro ? false : paraTodaLaEmpresa,
      activo,
    });
    if (!datos.success) {
      setError(datos.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    if (editando) actualizar.mutate({ id: editando.id, ...datos.data });
    else crear.mutate(datos.data);
  }

  return (
    <Modal
      titulo={editando ? `Editar el tipo ${editando.codigo}` : "Nuevo tipo de documento"}
      onCerrar={onCerrar}
      bloqueado={enviando}
    >
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <Campo
            etiqueta="Código"
            ayuda={
              naturalezaFija
                ? `Ya tiene ${editando?.documentos} documentos: no cambia.`
                : "De dos a cuatro letras."
            }
          >
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className={`${CLASE_ENTRADA} font-display tracking-wide uppercase`}
              maxLength={4}
              disabled={naturalezaFija}
              autoComplete="off"
              // biome-ignore lint/a11y/noAutofocus: el formulario se abre para escribir aquí
              autoFocus={!naturalezaFija}
            />
          </Campo>
          <Campo etiqueta="Nombre">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={CLASE_ENTRADA}
              autoComplete="off"
            />
          </Campo>
        </div>
        {naturalezaFija && (
          <Aviso tono="ambar">
            Este tipo ya tiene documentos: si es registro y si requiere aprobación no cambian,
            porque deciden cómo se publicaron sus revisiones. Si hace falta otro comportamiento,
            crea un tipo nuevo.
          </Aviso>
        )}
        <div className="space-y-2 text-[12.5px] text-texto-2">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={esRegistro}
              disabled={naturalezaFija}
              onChange={(e) => setEsRegistro(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Es un formato de registro.</span> Lleva control de
              registros (ubicación, retención, disposición), se llena en hojas y no tiene revisión
              anual ni acuses.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={requiereAprobacion}
              disabled={naturalezaFija}
              onChange={(e) => setRequiereAprobacion(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Pasa por la ruta de firmas</span> (elaboró, revisó,
              aprobó). Sin marcar es informativo: se publica directo, como un memorándum.
            </span>
          </label>
          {requiereAprobacion && (
            <label className="flex items-start gap-2 pl-6">
              <input
                type="checkbox"
                checked={conRevisor}
                onChange={(e) => setConRevisor(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold">Lleva revisor.</span> Sin marcar, sus documentos van
                con dos firmas: quien elabora firma y la revisión pasa directo a quien aprueba. Vale
                para las revisiones que se abran de ahora en adelante.
              </span>
            </label>
          )}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={esRegistro ? false : paraTodaLaEmpresa}
              disabled={esRegistro}
              onChange={(e) => setParaTodaLaEmpresa(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Es para toda la empresa.</span> Al publicarse lo ve y
              lo recibe todo el personal activo, no solo el del proceso, en nivel 1 o 2. Para
              comunicados y circulares.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={esRegistro ? false : tieneRevisionAnual}
              disabled={esRegistro}
              onChange={(e) => setTieneRevisionAnual(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Tiene revisión anual.</span> Un año después de
              aprobarse avisa que toca revisarlo.
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              className="mt-0.5"
            />
            <span>Activo (se ofrece al crear documentos)</span>
          </label>
        </div>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={enviando}>
            {enviando ? "Guardando…" : editando ? "Guardar cambios" : "Crear tipo"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

function TablaPuestos({
  puestos,
  onEditar,
}: {
  puestos: PuestoAdministrado[];
  onEditar: (p: PuestoAdministrado) => void;
}) {
  if (puestos.length === 0) return <EstadoVacio>No hay puestos.</EstadoVacio>;
  return (
    <Tabla minimo={720}>
      <thead>
        <tr className={CABECERA}>
          <th className={TH}>Código</th>
          <th className={TH}>Puesto</th>
          <th className={TH}>Proceso de la organización</th>
          <th className={TH}>Personas</th>
          <th className={TH} />
        </tr>
      </thead>
      <tbody>
        {puestos.map((p) => (
          <tr
            key={p.id}
            className={`border-b border-borde/60 last:border-0 ${p.activo ? "" : "opacity-60"}`}
          >
            <td className={`${TD} font-display font-semibold tracking-wide whitespace-nowrap`}>
              {p.codigo}
            </td>
            <td className={TD}>
              <span className="font-semibold">{p.nombre}</span>
              {!p.activo && (
                <Pildora tono="bg-gris-fondo text-gris" className="ml-2">
                  Desactivado
                </Pildora>
              )}
            </td>
            <td className={`${TD} text-[12px] text-texto-2`}>{p.area || "—"}</td>
            <td className={`${TD} text-texto-2`}>{p.personas}</td>
            <td className={`${TD} text-right`}>
              <Boton variante="secundario" onClick={() => onEditar(p)}>
                Editar
              </Boton>
            </td>
          </tr>
        ))}
      </tbody>
    </Tabla>
  );
}

function FormularioDePuesto({
  formulario,
  onCerrar,
  onGuardado,
}: {
  formulario: FormularioPuesto;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const trpc = useTRPC();
  const editando = formulario.modo === "editar" ? formulario.puesto : null;
  const [codigo, setCodigo] = useState(editando?.codigo ?? "");
  const [nombre, setNombre] = useState(editando?.nombre ?? "");
  const [area, setArea] = useState(editando?.area ?? "");
  const [activo, setActivo] = useState(editando?.activo ?? true);
  const [error, setError] = useState<string | null>(null);
  const opciones = { onSuccess: onGuardado, onError: (e: unknown) => setError(mensajeDe(e)) };
  const crear = useMutation(trpc.catalogos.crearPuesto.mutationOptions(opciones));
  const actualizar = useMutation(trpc.catalogos.actualizarPuesto.mutationOptions(opciones));
  const enviando = crear.isPending || actualizar.isPending;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const datos = esquemaPuesto.safeParse({ codigo, nombre, area, activo });
    if (!datos.success) {
      setError(datos.error.issues[0]?.message ?? "Revisa los datos");
      return;
    }
    if (editando) actualizar.mutate({ id: editando.id, ...datos.data });
    else crear.mutate(datos.data);
  }

  return (
    <Modal
      titulo={editando ? `Editar el puesto ${editando.codigo}` : "Nuevo puesto"}
      onCerrar={onCerrar}
      bloqueado={enviando}
    >
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <Campo etiqueta="Código" ayuda="Como en el listado: CCI-01.">
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className={`${CLASE_ENTRADA} font-display tracking-wide uppercase`}
              maxLength={7}
              autoComplete="off"
              // biome-ignore lint/a11y/noAutofocus: el formulario se abre para escribir aquí
              autoFocus
            />
          </Campo>
          <Campo etiqueta="Nombre" ayuda="Como va a salir en las firmas y los sellos.">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={CLASE_ENTRADA}
              autoComplete="off"
            />
          </Campo>
        </div>
        <Campo
          etiqueta="Proceso de la organización"
          ayuda="Tal como lo dice el listado aprobado. Opcional."
        >
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            className={CLASE_ENTRADA}
            autoComplete="off"
          />
        </Campo>
        {editando && editando.personas > 0 && nombre.trim() !== editando.nombre && (
          <Aviso tono="ambar">
            {editando.personas === 1
              ? "Una persona tiene este puesto: su puesto cambia con él."
              : `${editando.personas} personas tienen este puesto: su puesto cambia con él.`}{" "}
            Las firmas ya dadas conservan el nombre de su momento.
          </Aviso>
        )}
        <label className="flex items-center gap-2 text-[12.5px] text-texto-2">
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          Activo (se ofrece al dar de alta o editar a una persona)
        </label>
        {error && <Aviso>{error}</Aviso>}
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={enviando}>
            {enviando ? "Guardando…" : editando ? "Guardar cambios" : "Crear puesto"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
